import type { ID, QueryParams } from '@/types/common';

export const queryKeys = {
  products: {
    all: ['products'] as const,
    lists: () => [...queryKeys.products.all, 'list'] as const,
    list: (params?: QueryParams) => [...queryKeys.products.lists(), params] as const,
    detail: (id: ID) => [...queryKeys.products.all, 'detail', id] as const,
    stats: () => [...queryKeys.products.all, 'stats'] as const,
    inventoryStats: () => [...queryKeys.products.all, 'inventory-stats'] as const,
    lowStock: () => [...queryKeys.products.all, 'low-stock'] as const,
  },
  categories: {
    all: ['categories'] as const,
    list: () => [...queryKeys.categories.all, 'list'] as const,
  },
  sales: {
    all: ['sales'] as const,
    lists: () => [...queryKeys.sales.all, 'list'] as const,
    list: (params?: QueryParams) => [...queryKeys.sales.lists(), params] as const,
    stats: (params?: QueryParams) => [...queryKeys.sales.all, 'stats', params] as const,
    detail: (id: ID) => [...queryKeys.sales.all, 'detail', id] as const,
    receivables: () => [...queryKeys.sales.all, 'receivables'] as const,
  },
  quotes: {
    all: ['quotes'] as const,
    lists: () => [...queryKeys.quotes.all, 'list'] as const,
    list: (params?: QueryParams) => [...queryKeys.quotes.lists(), params] as const,
    detail: (id: ID) => [...queryKeys.quotes.all, 'detail', id] as const,
  },
  orders: {
    all: ['orders'] as const,
    lists: () => [...queryKeys.orders.all, 'list'] as const,
    list: (params?: QueryParams) => [...queryKeys.orders.lists(), params] as const,
  },
  customers: {
    all: ['customers'] as const,
    lists: () => [...queryKeys.customers.all, 'list'] as const,
    list: (params?: QueryParams) => [...queryKeys.customers.lists(), params] as const,
    detail: (id: ID) => [...queryKeys.customers.all, 'detail', id] as const,
  },
  expenses: {
    all: ['expenses'] as const,
    lists: () => [...queryKeys.expenses.all, 'list'] as const,
    list: (params?: QueryParams) => [...queryKeys.expenses.lists(), params] as const,
    detail: (id: ID) => [...queryKeys.expenses.all, 'detail', id] as const,
    byReference: (reference: string) =>
      [...queryKeys.expenses.all, 'byReference', reference] as const,
  },
  expenseCategories: {
    all: ['expense-categories'] as const,
    list: () => [...queryKeys.expenseCategories.all, 'list'] as const,
  },
  employees: {
    all: ['employees'] as const,
    lists: () => [...queryKeys.employees.all, 'list'] as const,
    list: (params?: QueryParams) => [...queryKeys.employees.lists(), params] as const,
    detail: (id: ID) => [...queryKeys.employees.all, 'detail', id] as const,
    adjustments: (id: ID) => [...queryKeys.employees.all, 'adjustments', id] as const,
  },
  dashboard: {
    all: ['dashboard'] as const,
    summary: (range?: string) => [...queryKeys.dashboard.all, 'summary', range] as const,
  },
  reports: {
    all: ['reports'] as const,
    sales: (params?: QueryParams) => [...queryKeys.reports.all, 'sales', params] as const,
    purchases: (params?: QueryParams) => [...queryKeys.reports.all, 'purchases', params] as const,
    expenses: (params?: QueryParams) => [...queryKeys.reports.all, 'expenses', params] as const,
    series: (type: string, year: number, from?: string, to?: string) =>
      [...queryKeys.reports.all, 'series', type, year, from ?? '', to ?? ''] as const,
    topProducts: (from?: string, to?: string) =>
      [...queryKeys.reports.all, 'top-products', from ?? '', to ?? ''] as const,
  },
  users: {
    all: ['users'] as const,
    list: () => [...queryKeys.users.all, 'list'] as const,
  },
  suppliers: {
    all: ['suppliers'] as const,
    list: () => [...queryKeys.suppliers.all, 'list'] as const,
  },
  auditLogs: {
    all: ['audit-logs'] as const,
    lists: () => [...queryKeys.auditLogs.all, 'list'] as const,
    list: (params?: QueryParams) => [...queryKeys.auditLogs.lists(), params] as const,
  },
  purchaseOrders: {
    all: ['purchase-orders'] as const,
    lists: () => [...queryKeys.purchaseOrders.all, 'list'] as const,
    list: (params?: QueryParams) => [...queryKeys.purchaseOrders.lists(), params] as const,
    stats: () => [...queryKeys.purchaseOrders.all, 'stats'] as const,
  },
  settings: {
    all: ['settings'] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    list: () => [...queryKeys.notifications.all, 'list'] as const,
  },
  profit: {
    all: ['profit'] as const,
    report: (from?: string, to?: string) =>
      [...queryKeys.profit.all, from ?? '', to ?? ''] as const,
  },
  valuation: {
    all: ['valuation'] as const,
    report: () => [...queryKeys.valuation.all, 'report'] as const,
  },
  partners: {
    all: ['partners'] as const,
    list: () => [...queryKeys.partners.all, 'list'] as const,
  },
  reviews: {
    all: ['reviews'] as const,
    list: () => [...queryKeys.reviews.all, 'list'] as const,
  },
  discover: {
    all: ['discover'] as const,
    list: () => [...queryKeys.discover.all, 'list'] as const,
  },
  projects: {
    all: ['projects'] as const,
    list: () => [...queryKeys.projects.all, 'list'] as const,
  },
} as const;
